import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Download, Copy, CheckCircle2, Laptop } from "lucide-react";
import { isMac, isSafari } from "@/lib/platform";
import { Button } from "@/components/ui/button";

const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallAppSection = () => {
  const [prompt, setPrompt] = useState(window.__lfInstallPrompt || null);
  const [installed, setInstalled] = useState(standalone());
  const link = window.location.origin;

  useEffect(() => {
    const onPrompt = () => setPrompt(window.__lfInstallPrompt);
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("lf-install-ready", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("lf-install-ready", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__lfInstallPrompt = null;
    setPrompt(null);
    if (outcome === "accepted") toast.success(isMac ? "LoreRight is being added to your Applications and Dock" : "LoreRight is being installed");
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied. Paste it into Chrome or Safari."); } catch { toast.error("Couldn't copy. Long-press the link to copy it."); }
  };

  return (
    <section className="mt-16 rounded-md border border-[#2D3442] bg-[#13171F] p-6 sm:p-8" data-testid="install-app-section">
      <div className="lf-label mb-2 flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" />Phone & desktop app</div>
      <h2 className="font-display text-3xl text-[#F3EBDD]">Install LoreRight on your phone or computer</h2>
      {installed ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-[#10B981]" data-testid="install-app-installed"><CheckCircle2 className="h-4 w-4" />You're using the installed app.</p>
      ) : (
        <>
          <p className="mt-2 max-w-xl text-sm text-[#A8A090]">No app store needed. It installs from your browser and opens full screen with its own icon.</p>
          {prompt && <Button className="mt-5" onClick={install} data-testid="install-app-button"><Download className="mr-1.5 h-4 w-4" />Install app</Button>}
          {isMac && isSafari && !prompt && <p className="mt-4 text-sm text-[#FBBF24]" data-testid="install-mac-safari-hint">On this Mac: in Safari's menu bar choose File → Add to Dock.</p>}
          <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <div className={isMac ? "order-first rounded-md border border-[#D97706]/40 bg-[#0F131A] p-3" : ""} data-testid="install-steps-mac">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-[#F3EBDD]"><Laptop className="h-4 w-4 text-[#F59E0B]" />Mac{isMac && <span className="rounded-full bg-[#D97706]/20 px-1.5 text-[10px] text-[#F59E0B]">This computer</span>}</div>
              <div className="text-xs font-medium text-[#E8DFD1]">Safari (macOS Sonoma 14 or newer)</div>
              <ol className="mb-2 list-decimal space-y-1 pl-5 text-sm text-[#A8A090]">
                <li>Open LoreRight in Safari.</li>
                <li>In the menu bar choose File → Add to Dock, then click Add.</li>
              </ol>
              <div className="text-xs font-medium text-[#E8DFD1]">Chrome or Edge</div>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-[#A8A090]">
                <li>Click the install icon at the right of the address bar, then Install.</li>
              </ol>
              <p className="mt-2 text-xs text-[#A8A090]">LoreRight then lives in your Dock, Launchpad and Applications folder. A Safari Dock app keeps its own storage, so sign in there once and turn on Offline access inside it.</p>
            </div>
            <div data-testid="install-steps-desktop">
              <div className="mb-2 text-sm font-medium text-[#F3EBDD]">Windows or Linux (Chrome / Edge)</div>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-[#A8A090]">
                <li>Open LoreRight in Chrome or Edge.</li>
                <li>Click the install icon at the right of the address bar.</li>
                <li>Click Install. LoreRight opens in its own window with a desktop and Start-menu icon.</li>
              </ol>
              <p className="mt-2 text-xs text-[#A8A090]">Pro and Lifetime: turn on Offline access in a universe's settings to write without internet.</p>
            </div>
            <div data-testid="install-steps-android">
              <div className="mb-2 text-sm font-medium text-[#F3EBDD]">Android (Chrome)</div>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-[#A8A090]">
                <li>Open the link below in the Chrome app itself, not inside another app.</li>
                <li>Tap the ⋮ menu at the top right.</li>
                <li>Tap Add to Home screen, then Install.</li>
              </ol>
            </div>
            <div data-testid="install-steps-ios">
              <div className="mb-2 text-sm font-medium text-[#F3EBDD]">iPhone / iPad (Safari)</div>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-[#A8A090]">
                <li>Open the link below in Safari.</li>
                <li>Tap the Share button.</li>
                <li>Tap Add to Home Screen, then Add.</li>
              </ol>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="min-w-0 truncate rounded border border-[#2D3442] bg-[#0B0D10] px-3 py-2 text-sm text-[#F59E0B] select-all" data-testid="install-app-link">{link}</code>
            <Button variant="outline" onClick={copy} data-testid="install-app-copy-button"><Copy className="mr-1.5 h-4 w-4" />Copy link</Button>
          </div>
        </>
      )}
    </section>
  );
};
